import axios from 'axios';

// Clave de API leída desde variables de entorno (REACT_APP_DEEPSEEK_API_KEY)
const API_KEY = process.env.REACT_APP_DEEPSEEK_API_KEY;
const isValidApiKey = !!API_KEY;

// URL base de la API de DeepSeek
const CHAT_URL = 'https://api.deepseek.com/v1/chat/completions';

/**
 * Obtiene una respuesta de la IA de DeepSeek
 * @param {Object} options - Opciones para la solicitud
 * @param {string} options.prompt - El prompt para la IA
 * @param {number} options.max_tokens - Número máximo de tokens en la respuesta
 * @param {number} options.temperature - Temperatura para la generación (0-1)
 * @returns {Promise<string>} - La respuesta generada por la IA
 */
export const getDeepSeekResponse = async ({ 
  prompt, 
  max_tokens = 500, 
  temperature = 0.7 
}) => {
  try {
    // Verificar si la API key es válida
    if (!isValidApiKey) {
      throw new Error('API key de DeepSeek no válida o no configurada. Por favor, configura una API key válida en las variables de entorno REACT_APP_DEEPSEEK_API_KEY.');
    }
    
    // Hacemos la llamada real a la API en todos los casos usando el formato de chat completions
    const response = await axios.post(
      CHAT_URL,
      {
        model: 'deepseek-chat',
        messages: [
          {
            role: 'system',
            content: 'Eres un asistente experto en análisis político y estrategia de comunicación.'
          },
          {
            role: 'user',
            content: prompt
          }
        ],
        max_tokens,
        temperature,
      },
      {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${API_KEY}`
        }
      }
    );
    
    // Verificar que la respuesta tenga el formato esperado
    if (!response.data || !response.data.choices || !response.data.choices[0] || !response.data.choices[0].message) {
      throw new Error('Formato de respuesta de DeepSeek inesperado: ' + JSON.stringify(response.data));
    }
    
    return response.data.choices[0].message.content;
  } catch (error) {
    console.error('Error al obtener respuesta de DeepSeek:', error);
    
    // Proporcionar un mensaje de error más detallado
    let errorMessage = 'Error al conectar con DeepSeek: ';
    
    if (error.response) {
      // Error de respuesta del servidor
      errorMessage += `Error ${error.response.status}: ${error.response.data?.error || JSON.stringify(error.response.data)}`;
    } else if (error.request) {
      // Error de red (no se recibió respuesta)
      errorMessage += 'No se pudo conectar con el servidor de DeepSeek. Verifica tu conexión a internet.';
    } else {
      // Error de configuración
      errorMessage += error.message || 'Error desconocido';
    }
    
    throw new Error(errorMessage);
  }
};

/**
 * Genera recomendaciones políticas basadas en un contexto específico
 * @param {Object} options - Opciones para la solicitud
 * @param {string} options.context - Contexto político para las recomendaciones
 * @returns {Promise<string>} - Recomendaciones generadas por la IA
 */
export const generatePoliticalRecommendations = async ({ context }) => {
  const prompt = `
    Como asesor político experto, analiza el siguiente contexto y proporciona recomendaciones 
    estratégicas concretas para mejorar la posición política:
    
    ${context}
    
    Proporciona 3-5 recomendaciones específicas, accionables y basadas en datos.
  `;
  
  return getDeepSeekResponse({ 
    prompt, 
    max_tokens: 800,
    temperature: 0.5
  });
};

/**
 * Analiza el sentimiento político de un texto
 * @param {string} text - Texto a analizar
 * @returns {Promise<Object>} - Análisis de sentimiento
 */
export const analyzePoliticalSentiment = async (text) => {
  try {
    // En desarrollo, simulamos el análisis
    if (process.env.NODE_ENV === 'development') {
      await new Promise(resolve => setTimeout(resolve, 800));
      
      // Análisis simulado basado en palabras clave
      const hasPositive = /\b(bueno|excelente|favorable|apoyo|éxito)\b/i.test(text);
      const hasNegative = /\b(malo|pésimo|fracaso|crítica|rechazo)\b/i.test(text);
      
      let sentiment = 'neutral';
      let score = 0;
      
      if (hasPositive && !hasNegative) {
        sentiment = 'positive';
        score = 0.7;
      } else if (hasNegative && !hasPositive) {
        sentiment = 'negative';
        score = -0.7;
      } else if (hasPositive && hasNegative) {
        sentiment = 'mixed';
        score = 0.1;
      }
      
      return {
        sentiment,
        score,
        analysis: "Análisis simulado en modo desarrollo"
      };
    }
    
    // En producción, llamada real a la API usando el formato de chat completions
    const response = await axios.post(
      CHAT_URL,
      {
        model: 'deepseek-chat',
        messages: [
          {
            role: 'system',
            content: 'Eres un asistente experto en análisis de sentimiento político. Analiza el texto y devuelve un objeto JSON con los campos "sentiment" (positive, negative, neutral o mixed), "score" (un número entre -1 y 1) y "analysis" (un breve análisis del texto).'
          },
          {
            role: 'user',
            content: `Analiza el siguiente texto: ${text}`
          }
        ],
        temperature: 0.3,
        max_tokens: 500
      },
      {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${API_KEY}`
        }
      }
    );
    
    // Intentamos parsear la respuesta como JSON
    try {
      const content = response.data.choices[0].message.content;
      return JSON.parse(content);
    } catch (parseError) {
      // Si no podemos parsear como JSON, devolvemos un objeto con el análisis en texto plano
      return {
        sentiment: 'neutral',
        score: 0,
        analysis: response.data.choices[0].message.content
      };
    }
  } catch (error) {
    console.error('Error al analizar sentimiento:', error);
    throw new Error('No se pudo analizar el sentimiento del texto');
  }
};
